import { Component, OnInit, inject } from "@angular/core";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { CerrixInviteComponent } from "@app/shared/cerrix-invite/cerrix-invite.component";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { InviteConfigModel, InviteModel, FormInviteModel } from "@models/invite/InviteModel";
import { FormInviteUser } from "@models/invite/InviteUser";
import { CerrixInviteDataService } from "@app/shared/services/cerrix-invite.service";
import { CerrixInviteType } from "@enums/invite/CerrixInviteType";
import { InviteMessage } from "@models/invite/InviteMessage";
import { FormInviteType } from "@app/forms/enums/FormInviteType";
import { InviteFormSendModel } from "@models/invite/InviteSendModel";
import { ToastrService } from "ngx-toastr";
import { Pages } from "@constants/pages/Pages";
import { finalize } from "rxjs/operators";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { firstValueFrom } from "rxjs";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/forms/i18n/scope";

@Component({
    selector: "form-invite",
    templateUrl: "./form-invite.component.html",
    styleUrls: ["./form-invite.component.scss"],
    imports: [CerrixLoaderComponent, CerrixInviteComponent, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class FormInviteComponent implements OnInit {
    private _dataService = inject(CerrixInviteDataService);
    private _toastrService = inject(ToastrService);
    private _pages = inject(Pages);
    private _promptService = inject(CerrixPromptService);
    private translation = injectTranslation(scope);

    tab: TabModel;
    id: number;

    inviteType = FormInviteType;

    targetedInvitationType: FormInviteType;
    inviteModelPromise: Promise<InviteModel>;
    inviteModel: FormInviteModel;
    inviteConfig: InviteConfigModel;

    async ngOnInit() {
        await this.translation.ready();
        if (isNaN(this.id)) {
            this.tab.close(false);
            this._toastrService.error(
                this.translation.t("formInvite.formIdRequiredMessage"),
                this.translation.t("formInvite.formIdRequiredTitle")
            );
            return;
        }

        let targetInvitation: FormInviteType;
        if (this.tab && this.tab.config) {
            targetInvitation = +this.tab.config.targetedinvitationtype;
            this.targetedInvitationType = targetInvitation;
        }

        this.inviteModelPromise = firstValueFrom(this._dataService
            .getInviteModel(CerrixInviteType.CerrixForm, this.id, null));

        this.inviteModelPromise.then((x) => {
            this.inviteModel = x as FormInviteModel;
            this.tab.name = this.translation.t("formInvite.inviteTabName", {
                title: this.inviteModel.targetName,
            });

            if (targetInvitation > 0) {
                this.loadType(targetInvitation);
            }
        });
    }

    async loadType(inviteType: FormInviteType) {
        await this.translation.ready();
        this.targetedInvitationType = inviteType;
        this.initConfig();
    }

    initConfig() {
        const config = new InviteConfigModel();

        config.tags = this.inviteModel.keywords;
        config.quickFilterProperties = [
            "displayName",
            "mail",
            "organizationName",
            "invitationStatus",
            "assignedObjectName",
        ];
        config.subject = this.inviteModel.subject;
        config.message = this.inviteModel.message;

        config.users = this.inviteModel.users;
        config.userSelection = [];

        config.templates = this.inviteModel.templates;
        config.organizations = this.inviteModel.organizations;

        config.onSaveTemplate = (template: InviteMessage) => {
            template.templateType = CerrixInviteType.CerrixForm;
            return firstValueFrom(this._dataService.storeTemplate(template));
        };

        config.onDeleteTemplate = async (id: number) => {
            await firstValueFrom(this._dataService.deleteTemplate(CerrixInviteType.CerrixForm, id));
            return true;
        };

        config.onSend = (subject: string, message: string, users: FormInviteUser[]) => {
            if (this.targetedInvitationType !== FormInviteType.ReminderInvitation) {
                if (users.length === 0) {
                    this._toastrService.warning(
                        this.translation.t("formInvite.noUsersSelectedMessage"),
                        this.translation.t("formInvite.noUsersSelectedTitle")
                    );
                    return;
                }
            }

            this._promptService
                .confirm(
                    this.translation.t("formInvite.sendInvitesTitle"),
                    this.translation.t("formInvite.sendInvitesMessage")
                )
                .onConfirm()
                .subscribe(() => {
                    if (
                        !subject ||
                        subject.trim().length === 0 ||
                        !message ||
                        message.trim().length === 0
                    ) {
                        this._toastrService.warning(
                            this.translation.t("formInvite.subjectMessageRequiredMessage"),
                            this.translation.t("formInvite.invitationErrorTitle")
                        );
                        return;
                    }

                    const prompt = this._promptService.loader(
                        this.translation.t("formInvite.sendingInvitesLoader")
                    );

                    const model = <InviteFormSendModel>{
                        subject,
                        message,
                        users: users.map((x) => x.ID),
                        inviteType: CerrixInviteType.CerrixForm,

                        formInviteType: this.targetedInvitationType,
                        formID: this.id,
                    };

                    firstValueFrom(this._dataService
                        .sendInvite(model.inviteType, model)
                        .pipe(finalize(() => prompt.close())))
                        .then(() => {
                            if (this.targetedInvitationType === FormInviteType.ReminderInvitation) {
                                this._toastrService.success(
                                    this.translation.t("formInvite.reminderSentMessage"),
                                    this.translation.t("formInvite.reminderSentTitle")
                                );
                            } else {
                                this._toastrService.success(
                                    this.translation.t("formInvite.invitedUsersMessage", {
                                        count: users.length,
                                    }),
                                    this.translation.t("formInvite.inviteSentTitle")
                                );
                            }

                            if (
                                this.tab.parent &&
                                this.tab.parent.lookupname === this._pages.Forms
                            ) {
                                this.tab.parent.refresh();
                                this.tab.parent.activate();
                                this.tab.close(false);
                            } else {
                                this.tab.config = {
                                    targetedinvitationtype: this.targetedInvitationType,
                                };
                                this.tab.refresh();
                            }
                        });
                });
        };

        let inviteTargetType = "";
        if (this.targetedInvitationType === FormInviteType.RequiredInvitation) {
            inviteTargetType = "required";
            config.users.forEach(
                (u: FormInviteUser) =>
                    (u.disabled = u.isInvited === true && u.inviteCompleted !== true)
            );
        } else if (this.targetedInvitationType === FormInviteType.ReminderInvitation) {
            inviteTargetType = "reminder";
            config.users = [];
        } else if (this.targetedInvitationType === FormInviteType.ShareInvitation) {
            inviteTargetType = "share";
        }

        config.inviteTargetMessage = this.translation.t("formInvite.inviteTargetMessage", {
            type: inviteTargetType,
            name: this.inviteModel.targetName,
        });

        this.inviteConfig = config;
    }
}
